import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext.jsx";
import { apiFetch } from "../lib/api.js";
import { useNavigate } from "react-router-dom";

// ... (keep your existing css variable exactly as is) ...
const css = `
.editor-wrap { max-width: 900px; margin: 40px auto; padding: 20px; }
.editor-card { background: #fff; padding: 30px; border-radius: 16px; border: 1px solid #e5e7eb; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }
.form-group { margin-bottom: 20px; }
.form-label { display: block; font-weight: 600; margin-bottom: 8px; font-size: 14px; color: #374151; }
.form-input, .form-textarea, .form-select { width: 100%; padding: 10px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 15px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }

/* Load Bar */
.load-bar { background: #f3f4f6; padding: 15px; border-radius: 12px; margin-bottom: 30px; display: flex; align-items: center; gap: 15px; border: 1px solid #e5e7eb; }
.load-label { font-size: 13px; font-weight: 700; text-transform: uppercase; color: #6b7280; white-space: nowrap; }

/* Blocks */
.block-list { margin-top: 30px; border-top: 2px solid #f3f4f6; padding-top: 20px; }
.block-item { background: #f9fafb; padding: 16px; border-radius: 8px; border: 1px solid #e5e7eb; margin-bottom: 12px; position: relative; }
.block-header { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 12px; text-transform: uppercase; font-weight: 700; color: #9ca3af; }
.btn-del { color: #ef4444; cursor: pointer; border: none; background: none; font-weight: 600; }

/* Toolbar */
.toolbar { display: flex; gap: 10px; margin-bottom: 30px; flex-wrap: wrap; }
.tool-btn { padding: 8px 16px; background: #fff; border: 1px solid #d1d5db; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 13px; transition: all 0.2s; }
.tool-btn:hover { border-color: #6E5BFF; color: #6E5BFF; }

/* Sticky Footer */
.action-bar { position: sticky; bottom: 20px; background: #111827; padding: 16px; border-radius: 12px; display: flex; justify-content: space-between; align-items: center; color: #fff; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); margin-top: 40px; }
.save-btn { background: #6E5BFF; color: #fff; border: none; padding: 10px 24px; border-radius: 8px; font-weight: 700; cursor: pointer; }
.preview-btn { background: rgba(255,255,255,0.1); color: #fff; border: 1px solid rgba(255,255,255,0.2); padding: 10px 20px; border-radius: 8px; font-weight: 600; cursor: pointer; margin-right: 10px; }
`;

export default function AdminBlogEditor() {
  const { user } = useAuth();
  
  // State
  const [loading, setLoading] = useState(false);
  const [existingPosts, setExistingPosts] = useState([]);
  
  // Form Fields
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [category, setCategory] = useState("Music Marketing");
  const [published, setPublished] = useState(false);
  const [blocks, setBlocks] = useState([]);

  // 1. Fetch list on mount
  useEffect(() => {
    fetchPostList();
  }, []);

  const fetchPostList = async () => {
    try {
      const data = await apiFetch('/api/admin/blog/list');
      setExistingPosts(data.items || []);
    } catch (e) {
      console.error("Failed to load post list", e);
    }
  };

  // 2. Load a specific post
  const loadPost = async (selectedSlug) => {
    if (!selectedSlug) return resetForm();
    
    setLoading(true);
    try {
      const safeSlug = encodeURIComponent(selectedSlug);
      const data = await apiFetch(`/api/admin/editor-data/${safeSlug}`);
      
      const p = data.item;
      setSlug(p.slug);
      setTitle(p.title);
      setExcerpt(p.excerpt || "");
      setCategory(p.category || "Music Marketing");
      setPublished(!!p.published);
      
      // Ensure blocks have unique IDs for React keys
      setBlocks((p.content || []).map(b => ({ 
        ...b, 
        id: b.id || Date.now() + Math.random() 
      }))); 
    } catch (e) {
      console.error(e);
      alert("Error loading post: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSlug("");
    setTitle("");
    setExcerpt("");
    setCategory("Music Marketing");
    setPublished(false);
    setBlocks([]);
  };

  // Auto-generate slug from title ONLY if creating new (slug is empty)
  useEffect(() => {
    if (!slug && title) {
      setSlug(title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
    }
  }, [title]);

  const addBlock = (type) => {
    const newBlock = { type, id: Date.now() };
    if (type === 'ul') newBlock.items = []; 
    if (type === 'cta') {
      newBlock.title = "Ready to promote?"; 
      newBlock.text = "Find playlists now."; 
      newBlock.btnText = "Start Free"; 
      newBlock.link = "/signup";
    }
    else { newBlock.text = ""; }
    setBlocks([...blocks, newBlock]);
  };

  const updateBlock = (id, field, value) => {
    setBlocks(blocks.map(b => b.id === id ? { ...b, [field]: value } : b));
  };

  const removeBlock = (id) => {
    setBlocks(blocks.filter(b => b.id !== id));
  };

  const handleSave = async (isPublishing) => {
    if (!slug || !title) return alert("Title and Slug are required");
    setLoading(true);

    try {
      const payload = {
        slug,
        title,
        excerpt,
        category,
        published: isPublishing ? true : published,
        content: blocks
      };

      await apiFetch('/api/admin/blog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      alert("Saved!");
      fetchPostList(); // Refresh dropdown
    } catch (e) {
      console.error(e);
      alert("Error saving: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    await handleSave(false);
    window.open(`/blog/${slug}`, '_blank');
  };

  if (!user || user.email !== 'dev@musicbase.io') return <div className="p-10">Admin only</div>;

  return (
    <div className="editor-wrap">
      <style>{css}</style>
      
      {/* SELECT POST DROPDOWN */}
      <div className="load-bar">
        <span className="load-label">Edit Post:</span>
        <select 
          className="form-select" 
          onChange={(e) => loadPost(e.target.value)}
          value={existingPosts.some(p => p.slug === slug) ? slug : ""}
        >
          <option value="">+ Create New Post</option>
          {existingPosts.map(p => (
            <option key={p.slug} value={p.slug}>
              {p.published ? "✅" : "📝 Draft:"} {p.title}
            </option>
          ))}
        </select>
      </div>

      <div className="editor-card">
        <h1 style={{fontSize:'24px', fontWeight:'800', marginBottom:'24px'}}>
          {slug && existingPosts.some(p => p.slug === slug) ? 'Edit Post' : 'Create New Post'}
        </h1>
        
        <div className="form-group">
          <label className="form-label">Post Title</label>
          <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Title..." />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Slug (URL)</label>
            <input className="form-input" value={slug} onChange={e => setSlug(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Category</label>
            <select className="form-input" value={category} onChange={e => setCategory(e.target.value)}>
              <option>Music Marketing</option>
              <option>Spotify Tips</option>
              <option>Industry News</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Short Excerpt (SEO Description)</label>
          <textarea className="form-textarea" rows="2" value={excerpt} onChange={e => setExcerpt(e.target.value)} />
        </div>

        <hr style={{margin:'30px 0', border:'none', borderTop:'1px solid #eee'}} />

        <h3 className="form-label" style={{fontSize:'16px'}}>Content Blocks</h3>
        
        <div className="toolbar">
          <button className="tool-btn" onClick={() => addBlock('h2')}>+ H2</button>
          <button className="tool-btn" onClick={() => addBlock('p')}>+ Text</button>
          <button className="tool-btn" onClick={() => addBlock('ul')}>+ List</button>
          <button className="tool-btn" onClick={() => addBlock('blockquote')}>+ Quote</button>
          <button className="tool-btn" style={{borderColor:'#6E5BFF', color:'#6E5BFF'}} onClick={() => addBlock('cta')}>+ CTA</button>
        </div>

        <div className="block-list">
          {blocks.map((block, index) => (
            <div key={block.id} className="block-item">
              <div className="block-header">
                <span>{block.type}</span>
                <button className="btn-del" onClick={() => removeBlock(block.id)}>Delete</button>
              </div>

              {block.type === 'h2' && (
                <input className="form-input" placeholder="Subheading..." value={block.text} onChange={(e) => updateBlock(block.id, 'text', e.target.value)} />
              )}
              
              {(block.type === 'p' || block.type === 'blockquote') && (
                <textarea className="form-textarea" rows="4" placeholder="Content..." value={block.text} onChange={(e) => updateBlock(block.id, 'text', e.target.value)} />
              )}

              {block.type === 'ul' && (
                <div>
                   <p style={{fontSize:'12px', color:'#666', marginBottom:'5px'}}>Items (one per line):</p>
                   <textarea className="form-textarea" rows="4" value={Array.isArray(block.items) ? block.items.join('\n') : block.items} onChange={(e) => updateBlock(block.id, 'items', e.target.value.split('\n'))} />
                </div>
              )}

              {block.type === 'cta' && (
                <div style={{background:'#f0fdf4', padding:'10px', borderRadius:'8px'}}>
                  <input className="form-input" style={{marginBottom:'8px'}} placeholder="CTA Title" value={block.title} onChange={e => updateBlock(block.id, 'title', e.target.value)} />
                  <textarea className="form-textarea" style={{marginBottom:'8px'}} rows="2" placeholder="CTA Text" value={block.text} onChange={e => updateBlock(block.id, 'text', e.target.value)} />
                  <div className="form-row">
                    <input className="form-input" placeholder="Button Label" value={block.btnText} onChange={e => updateBlock(block.id, 'btnText', e.target.value)} />
                    <input className="form-input" placeholder="Button Link (/signup)" value={block.link} onChange={e => updateBlock(block.id, 'link', e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="action-bar">
        <div>
          <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer'}}>
            <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
            <span>Publicly Visible?</span>
          </label>
        </div>
        <div>
          <button className="preview-btn" onClick={handlePreview}>Save & Preview</button>
          <button className="save-btn" onClick={() => handleSave(false)} disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}